import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as superAdminApi from '../api/superAdminApi';

const SuperAdminContext = createContext(null);

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within a SuperAdminProvider');
  }
  return context;
};

export const SuperAdminProvider = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState(null); // { tempToken, username }
  const [pending3FA, setPending3FA] = useState(null); // { tempToken, maskedEmail }
  const navigate = useNavigate();

  // Beim Start prüfen ob Super-Admin eingeloggt ist
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('superAdminToken');
      if (token) {
        try {
          const data = await superAdminApi.getMe();
          setSuperAdmin(data);
        } catch (error) {
          console.error('Super-Admin Auth Check failed:', error);
          localStorage.removeItem('superAdminToken');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (username, passwort, totpCode = null) => {
    try {
      const data = await superAdminApi.login(username, passwort, totpCode);

      // Prüfen ob 2FA erforderlich
      if (data.requires2FA) {
        setPending2FA({
          tempToken: data.tempToken,
          username
        });
        return { success: true, requires2FA: true };
      }

      // Prüfen ob 3FA erforderlich (nach Login ohne 2FA aber mit 3FA)
      if (data.requires3FA) {
        setPending3FA({
          tempToken: data.tempToken,
          maskedEmail: data.maskedEmail
        });
        return { success: true, requires3FA: true, maskedEmail: data.maskedEmail };
      }

      // Normaler Login (ohne 2FA/3FA oder mit totpCode)
      localStorage.setItem('superAdminToken', data.token);
      setSuperAdmin(data.superAdmin);
      setPending2FA(null);
      setPending3FA(null);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login fehlgeschlagen'
      };
    }
  };

  const verify2FA = async (totpCode) => {
    if (!pending2FA) {
      return { success: false, error: 'Keine 2FA-Verifizierung ausstehend' };
    }

    try {
      const data = await superAdminApi.verify2FA(pending2FA.tempToken, totpCode);

      // Nach 2FA kann noch 3FA kommen
      if (data.requires3FA) {
        setPending2FA(null);
        setPending3FA({
          tempToken: data.tempToken,
          maskedEmail: data.maskedEmail
        });
        return { success: true, requires3FA: true, maskedEmail: data.maskedEmail, usedBackupCode: data.usedBackupCode };
      }

      localStorage.setItem('superAdminToken', data.token);
      setSuperAdmin(data.superAdmin);
      setPending2FA(null);
      setPending3FA(null);
      return { success: true, usedBackupCode: data.usedBackupCode };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || '2FA-Verifizierung fehlgeschlagen'
      };
    }
  };

  const verify3FA = async (emailCode) => {
    if (!pending3FA) {
      return { success: false, error: 'Keine 3FA-Verifizierung ausstehend' };
    }

    try {
      const data = await superAdminApi.verify3FA(pending3FA.tempToken, emailCode);
      localStorage.setItem('superAdminToken', data.token);
      setSuperAdmin(data.superAdmin);
      setPending3FA(null);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || '3FA-Verifizierung fehlgeschlagen'
      };
    }
  };

  const cancel2FA = () => {
    setPending2FA(null);
    setPending3FA(null);
  };

  const logout = () => {
    localStorage.removeItem('superAdminToken');
    setSuperAdmin(null);
    navigate('/super-admin/login');
  };

  const impersonate = async (organisationId) => {
    try {
      const data = await superAdminApi.impersonate(organisationId);

      // Normalen User-Token setzen (für die App)
      localStorage.setItem('token', data.token);

      // Flag setzen dass wir impersoniert sind
      localStorage.setItem('isImpersonating', 'true');
      localStorage.setItem('impersonatedOrgName', data.organisation.name);

      // Zur App navigieren
      window.location.href = '/dashboard';

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Impersonate fehlgeschlagen'
      };
    }
  };

  // Scope des aktuellen Super-Admins (WERKPILOT oder HEYKIKI)
  const scope = superAdmin?.scope || 'WERKPILOT';
  const isHeyKikiScope = scope === 'HEYKIKI';

  const value = {
    superAdmin,
    loading,
    login,
    verify2FA,
    verify3FA,
    cancel2FA,
    pending2FA,
    pending3FA,
    logout,
    impersonate,
    isAuthenticated: !!superAdmin,
    scope,
    isHeyKikiScope
  };

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export default SuperAdminContext;
